/**
 * Small info-tooltip affordance: a focusable icon button that reveals a hint
 * on hover/focus. Consolidates the raw `<button>` + lucide `Info` + `Tooltip`
 * pattern that several settings forms had copy-pasted. Self-contained — brings
 * its own `TooltipProvider` so callers can drop it in anywhere.
 *
 * Pass the tooltip text as `children` (or `content`). `aria-label` defaults to
 * the field `label` (rendered as "<label> info") when given, else "More info".
 */

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@cometchat-ai/ui-dashboard";
import { Info } from "./icons";
import type { ReactNode } from "react";

export function InfoHint({
  children,
  content,
  label,
  className,
}: {
  children?: ReactNode;
  content?: ReactNode;
  label?: string;
  className?: string;
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={label ? `${label} info` : "More info"}
            className="text-muted-foreground hover:text-foreground"
          >
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent className={className ?? "max-w-xs text-xs"}>
          {content ?? children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
